import { Injectable, inject } from '@angular/core';
import { Auth, browserLocalPersistence, signInWithCustomToken } from '@angular/fire/auth';
import { Router } from '@angular/router';
import { setPersistence } from 'firebase/auth';
import jwtDecode from 'jwt-decode';
import { Subject } from 'rxjs';
import { FunctionsService } from '../functions/functions.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private auth: Auth = inject(Auth);
  private logoutStartedSubject = new Subject<void>();
  private isLoggingOut = false;
  readonly logoutStarted$ = this.logoutStartedSubject.asObservable();

  constructor(
    private functionsService: FunctionsService,
    private router: Router,
  ) { }

  async login(username: string, password: string) {
    await setPersistence(this.auth, browserLocalPersistence);

    const customToken = await this.functionsService.callFunction('createCustomToken', { username, password });

    localStorage.setItem('token', customToken.data as string);
    // Initialize activity tracking on login
    localStorage.setItem('lastActivityTime', new Date().toISOString());
    await signInWithCustomToken(this.auth, customToken.data as string);
  }

  async logout(): Promise<boolean> {
    if (this.isLoggingOut) {
      return false;
    }

    const previousUrl = this.router.url;
    let authenticatedResourcesStopped = false;
    this.isLoggingOut = true;

    try {
      const transitioned = await this.router.navigate(['/cerrando-sesion'], { replaceUrl: true });
      if (!transitioned) {
        return false;
      }

      this.logoutStartedSubject.next();
      authenticatedResourcesStopped = true;
      await this.auth.signOut();
      localStorage.clear();

      const navigatedToLogin = await this.router.navigate(['/login'], { replaceUrl: true });
      if (!navigatedToLogin) {
        window.location.replace('/login');
      }

      return true;
    } catch {
      if (authenticatedResourcesStopped && this.auth.currentUser) {
        window.location.replace(previousUrl);
      } else if (!this.auth.currentUser) {
        window.location.replace('/login');
      }

      return false;
    } finally {
      this.isLoggingOut = false;
    }
  }

  get logoutInProgress(): boolean {
    return this.isLoggingOut;
  }

  userIsLoggedIn() {
    return this.auth.currentUser != null;
  }

  getCurrentUser(): any {
    return jwtDecode(localStorage.getItem('token') as string);
  }

  getCurrentUserFullName(): string {
    return this.getCurrentUserClaim('fullName');
  }

  getCurrentUserPhotoURL(): string {
    return this.getCurrentUserClaim('photoURL');
  }

  getCurrentUserClaim(claim: string) {
    return this.getCurrentUser().claims[claim];
  }

  hasLongRunningSession(): boolean {
    const token = localStorage.getItem('token');

    if (!token) {
      return false;
    }

    try {
      const decodedToken: any = jwtDecode(token);
      return decodedToken.claims?.sessionDuration === 24;
    } catch {
      return false;
    }
  }

  getCurrentUserOrganization(): { name: string, shortName: string } {
    return this.getCurrentUserClaim('organization');
  }

  getCurrentUserEmail(): string[] {
    return this.getCurrentUserClaim('email')
  }

  getCurrentUserRoles(): string[] {
    return this.getCurrentUserClaim('roles')

  }

  getCurrentUserMqttId(): string {
    return this.getCurrentUserClaim('mqttId')
  }

  userHasRole(role: string): boolean {
    return this.getCurrentUserRoles().indexOf(role) != -1;
  }
}
